import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AlertCircle, RefreshCw, Search } from 'lucide-react';
import { PageHeader } from '@/components/layout/page-header';
import { BusyOverlay } from '@/components/ui/busy-overlay';
import { Button } from '@/components/ui/button';
import { CopyButton } from '@/components/ui/copy-button';
import { FilterBar, type FilterField } from '@/components/ui/filter-bar';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { formatDate, formatPhone } from '@/lib/format';
import { cn } from '@/lib/utils';
import { RequestDetailSheet } from './request-detail-sheet';
import { StatusBadge } from './status-badge';
import { useAccountRequests } from './account-requests.api';
import { REQUEST_TYPE, fullName, statusOf, type AccountRequest } from './types';

/**
 * Account requests — the queue of people asking to be let in.
 *
 * The page opens on the pending ones, which is the only reason anybody comes
 * here; the other four filters are for looking something up afterwards.
 *
 * Three things the original did that are not repeated:
 *
 *  - It ran `search('')` from a mount effect *and* `search(searchTerm)` from an
 *    effect keyed on the type filter, so every load fired the same request
 *    twice. The term and the filter are the query key now, and one request
 *    answers both.
 *  - It tracked `window.innerWidth` in state with `[window.innerWidth]` as the
 *    dependency array — a value that is not reactive, so the effect never re-ran
 *    and the drawer kept whatever width the first render happened to see. The
 *    layout is CSS.
 *  - It redirected non-support accounts with `history.push('/')` in the middle
 *    of the render. The route guard does it before the page mounts.
 */

export function AccountRequestsPage() {
  const { t } = useTranslation();

  // Read once, as the initial state: after this the controls own it.
  const [params] = useSearchParams();
  const [draft, setDraft] = useState(() => params.get('q') ?? '');
  const [query, setQuery] = useState(() => params.get('q') ?? '');
  const [type, setType] = useState(
    () => params.get('type') ?? String(REQUEST_TYPE.pending)
  );
  const [selected, setSelected] = useState<AccountRequest | null>(null);

  const list = useAccountRequests(query, Number(type));
  const rows = list.data ?? [];

  const filterFields: FilterField[] = [
    {
      id: 'type',
      label: t('accountRequests.showing'),
      value: type,
      onChange: setType,
      // Pending → approved → rejected → registered is a lifecycle, not a set
      // of names to alphabetise.
      sort: false,
      resetValue: String(REQUEST_TYPE.pending),
      options: [
        {
          value: String(REQUEST_TYPE.pending),
          label: t('accountRequests.status.pending'),
        },
        {
          value: String(REQUEST_TYPE.approved),
          label: t('accountRequests.status.approved'),
        },
        {
          value: String(REQUEST_TYPE.rejected),
          label: t('accountRequests.status.rejected'),
        },
        {
          value: String(REQUEST_TYPE.registered),
          label: t('accountRequests.status.registered'),
        },
        { value: String(REQUEST_TYPE.all), label: t('accountRequests.all') },
      ],
    },
  ];

  /** The same cell in both layouts, so they cannot drift apart. */
  const Applicant = ({ row }: { row: AccountRequest }) => (
    <div className="min-w-0">
      <p className="truncate font-medium">
        {fullName(row) || t('accountRequests.noName')}
      </p>
      {row.businessName && (
        <p className="text-muted-foreground truncate text-xs">
          {row.businessName}
        </p>
      )}
    </div>
  );

  return (
    <div className="space-y-6">
      <PageHeader
        title={t('accountRequests.title')}
        description={t('accountRequests.subtitle')}
        actions={
          <Button
            variant="outline"
            size="icon-sm"
            aria-label={t('common.reload')}
            disabled={list.isFetching}
            onClick={() => void list.refetch()}
          >
            <RefreshCw className={list.isFetching ? 'animate-spin' : ''} />
          </Button>
        }
      />

      <FilterBar
        busy={list.isFetching}
        search={
          <form
            role="search"
            onSubmit={(event) => {
              event.preventDefault();
              // Re-submitting the same term is a deliberate refresh, and with
              // the term as the query key nothing else would happen.
              if (draft === query) void list.refetch();
              setQuery(draft);
            }}
            className="flex gap-2"
          >
            <div className="relative flex-1">
              <Search className="text-muted-foreground pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2" />
              <Input
                value={draft}
                onChange={(event) => setDraft(event.target.value)}
                placeholder={t('accountRequests.searchPlaceholder')}
                aria-label={t('accountRequests.searchPlaceholder')}
                className="h-10 pl-9"
              />
            </div>
            <Button type="submit" className="h-10">
              <Search className="sm:hidden" />
              <span className="hidden sm:inline">{t('common.search')}</span>
            </Button>
          </form>
        }
        fields={filterFields}
        summary={
          list.isSuccess ? (
            <p className="text-muted-foreground text-sm" aria-live="polite">
              {t('accountRequests.resultCount', { count: rows.length })}
            </p>
          ) : null
        }
      />

      {list.isError && (
        <div className="border-destructive/30 bg-destructive/8 text-destructive flex items-start gap-2 rounded-xl border p-4 text-sm">
          <AlertCircle className="mt-0.5 size-4 shrink-0" />
          <div className="space-y-2">
            <p>{t('accountRequests.loadFailed')}</p>
            <Button
              variant="outline"
              size="sm"
              onClick={() => void list.refetch()}
            >
              {t('common.retry')}
            </Button>
          </div>
        </div>
      )}

      {list.isPending && (
        <div className="divide-y rounded-xl border">
          {Array.from({ length: 6 }).map((_, index) => (
            <div key={index} className="flex items-center gap-3 p-3">
              <div className="flex-1 space-y-1.5">
                <Skeleton className="h-4 w-44" />
                <Skeleton className="h-3 w-56" />
              </div>
              <Skeleton className="h-5 w-20" />
            </div>
          ))}
        </div>
      )}

      {list.isSuccess && rows.length === 0 && (
        <div className="relative rounded-xl">
          <BusyOverlay show={list.isFetching} />
          <p className="text-muted-foreground rounded-xl border border-dashed py-14 text-center text-sm">
            {query.trim()
              ? t('accountRequests.noResults', { term: query })
              : t('accountRequests.noneInQueue')}
          </p>
        </div>
      )}

      {rows.length > 0 && (
        <>
          {/* Desktop: a table. */}
          <div className="hidden rounded-xl border lg:block">
            <Table busy={list.isFetching}>
              <TableHeader>
                <TableRow className="hover:bg-transparent">
                  <TableHead>{t('accountRequests.applicant')}</TableHead>
                  <TableHead>{t('accountRequests.email')}</TableHead>
                  <TableHead>{t('accountRequests.phone')}</TableHead>
                  <TableHead>{t('accountRequests.requestedOn')}</TableHead>
                  <TableHead>{t('accountRequests.statusColumn')}</TableHead>
                  <TableHead className="w-20 text-right" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((row) => (
                  <TableRow key={row.id} className="group">
                    <TableCell>
                      <Applicant row={row} />
                    </TableCell>
                    <TableCell>
                      {row.email ? (
                        <span className="flex items-center gap-1">
                          <span className="max-w-[16rem] truncate">
                            {row.email}
                          </span>
                          <CopyButton value={row.email} />
                        </span>
                      ) : (
                        '—'
                      )}
                    </TableCell>
                    <TableCell className="whitespace-nowrap">
                      {formatPhone(row.phoneNumber)}
                    </TableCell>
                    <TableCell className="whitespace-nowrap">
                      {formatDate(row.createdAt)}
                    </TableCell>
                    <TableCell>
                      <StatusBadge status={statusOf(row)} />
                    </TableCell>
                    <TableCell className="text-right">
                      <Button size="sm" onClick={() => setSelected(row)}>
                        {t('accountRequests.view')}
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>

          {/* Phone: cards. A six-column table in a sideways scroller is not a
              mobile layout — the original made you scroll to reach the status,
              which is the one column anybody comes here for. */}
          <div className="relative lg:hidden">
            <BusyOverlay show={list.isFetching} />
            <div
              className={cn(
                'space-y-2',
                list.isFetching && 'opacity-60 transition-opacity'
              )}
            >
              {rows.map((row) => (
                <div
                  key={row.id}
                  className="bg-card space-y-3 rounded-xl border p-3"
                >
                  <div className="flex items-start justify-between gap-3">
                    <Applicant row={row} />
                    <StatusBadge status={statusOf(row)} />
                  </div>
                  <div className="text-muted-foreground space-y-0.5 text-xs">
                    {row.email && <p className="truncate">{row.email}</p>}
                    <p>
                      {formatPhone(row.phoneNumber)} ·{' '}
                      {formatDate(row.createdAt)}
                    </p>
                  </div>
                  <Button
                    size="sm"
                    className="w-full"
                    onClick={() => setSelected(row)}
                  >
                    {t('accountRequests.view')}
                  </Button>
                </div>
              ))}
            </div>
          </div>
        </>
      )}

      <RequestDetailSheet
        request={selected}
        open={selected !== null}
        onOpenChange={(next) => {
          if (!next) setSelected(null);
        }}
      />
    </div>
  );
}
